/*
 * Copyright (c) 2026 Ambits Technologies LLC. All rights reserved.
 *
 * This source code is the proprietary and confidential property of
 * Ambits Technologies LLC. Unauthorized copying, modification, distribution,
 * or use of this software, via any medium, is strictly prohibited.
 *
 * MyAmbits - AI-Powered Location Safety & Social Platform
 * https://myambits.com
 */

/*
 * The single phone-normalization primitive for every Twilio-bound number:
 * emergency-contact add/edit, Walk Me Home watchers, and both server-side
 * send/match routes (api/sms-send.ts, api/sms-inbound.ts).
 *
 * Built on libphonenumber-js so a number is parsed against its actual
 * country, never guessed by string-concatenating a "+". A bare national
 * number ("9876543210") needs `defaultCountry` to resolve correctly — pass
 * the device/account region, falling back to "US" only when neither is
 * known. A number that already carries an explicit "+<code>" prefix parses
 * correctly regardless of `defaultCountry`.
 *
 * `src/utils/phone.ts::normalizeToE164` (the friend-discovery / phone-auth
 * contact-matching helper) delegates to this same primitive — there is one
 * parsing implementation, not two.
 */

import {
  parsePhoneNumberFromString,
  type CountryCode,
} from "libphonenumber-js";

export type NormalizePhoneResult = { e164: string } | { error: string };

const INVALID_MESSAGE = "Enter a valid phone number.";

export function normalizePhone(
  input: string,
  defaultCountry: CountryCode,
): NormalizePhoneResult {
  if (typeof input !== "string" || input.trim().length === 0) {
    return { error: INVALID_MESSAGE };
  }
  try {
    const parsed = parsePhoneNumberFromString(input, defaultCountry);
    if (!parsed || !parsed.isValid()) {
      return { error: INVALID_MESSAGE };
    }
    return { e164: parsed.number };
  } catch {
    return { error: INVALID_MESSAGE };
  }
}

/**
 * Last-4-digits mask for logging a phone number. Never log a full number —
 * every Twilio send/match failure log uses this.
 */
export function maskPhone(value: string): string {
  if (typeof value !== "string" || value.length === 0) return "(none)";
  return `…${value.slice(-4)}`;
}

/**
 * Detects the country from an input that already carries an explicit
 * "+<code>" prefix — e.g. "+91 98765 43210" → "IN". Returns null for
 * anything else (no leading "+", unparsable, or no leading "+"-prefixed
 * country resolves). Used by the country-picker UI to auto-switch the
 * selected country to match what the user actually typed, so typing an
 * explicit country code always wins over whatever was previously picked.
 *
 * This never guesses: a bare national number ("9876543210") has no
 * explicit code to detect, so it correctly returns null rather than
 * assuming one.
 */
export function detectCountryFromInput(input: string): CountryCode | null {
  if (typeof input !== "string" || !input.trim().startsWith("+")) {
    return null;
  }
  try {
    const parsed = parsePhoneNumberFromString(input);
    return parsed?.country ?? null;
  } catch {
    return null;
  }
}

/**
 * Splits an already-stored E.164 number back into {country, nationalNumber}
 * so an edit form can pre-select the right country-picker option and show
 * the number in national format, instead of re-showing the raw "+91…"
 * string with no country context. Returns null if it doesn't parse.
 */
export function splitE164(
  e164: string,
): { country: CountryCode; nationalNumber: string } | null {
  try {
    const parsed = parsePhoneNumberFromString(e164);
    if (!parsed || !parsed.country) return null;
    return { country: parsed.country, nationalNumber: parsed.formatNational() };
  } catch {
    return null;
  }
}
